/**
 * VIVA HUB Audit Log app — cross-app activity trail
 */

import React, { useEffect, useState, useCallback } from 'react'
import HubLayout from '../components/hub/HubLayout'
import {
  DatatableCard,
  DatatableEmpty,
  DatatableScroll,
  DatatableTh,
  DATATABLE_CELL_CLASS,
  DATATABLE_TABLE_CLASS,
  DATATABLE_TBODY_CLASS,
  DATATABLE_THEAD_CLASS,
  useClientDatatable,
} from '../components/shared/Datatable'
import DatatablePagination from '../components/shared/DatatablePagination'
import { Button } from '@/components/ui/button'
import { reportsAPI } from '../utils/scheduler/api'
import { HUB_APP_LABELS } from '../constants/hub-apps'
import { formatDbDateTime, parseDbDateTime } from '../utils/datetime'
import {
  formatAuditDetailsPreview,
  formatAuditDetailsRaw,
  formatAuditDetailsSummary,
  formatAuditIpAddress,
  formatAuditEntityLabel,
  getAuditDetailsChanges,
} from '../utils/audit-display'
import { useToast } from '../contexts/ToastContext'

function AuditLogDetailModal({ log, onClose }) {
  const { success: toastSuccess, error: toastError } = useToast()
  useEffect(() => {
    if (!log) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [log, onClose])

  if (!log) return null

  const summary = formatAuditDetailsSummary(log)
  const changes = getAuditDetailsChanges(log)
  const rawDetails = formatAuditDetailsRaw(log)
  const entityLabel = formatAuditEntityLabel(log)
  const displayIp = log.ip_address ? formatAuditIpAddress(log.ip_address) : null
  const ipIsMapped =
    displayIp && log.ip_address && displayIp !== log.ip_address && !log.ip_address.includes('.')

  const copyDetails = async () => {
    const text = [summary, changes.length ? formatChangesText(changes) : '', rawDetails]
      .filter(Boolean)
      .join('\n\n')
    try {
      await navigator.clipboard.writeText(text)
      toastSuccess('Details copied to clipboard.')
    } catch {
      toastError('Could not copy details. Select the text and copy manually.')
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="audit-log-detail-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id="audit-log-detail-title" className="text-lg font-semibold text-gray-900">
              Audit log details
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Entry #{log.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 border-b border-gray-100 px-5 py-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">When</dt>
            <dd className="font-medium text-gray-900">{formatDbDateTime(log.created_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">App</dt>
            <dd className="font-medium text-gray-900">{formatAppSource(log.app_source)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">User</dt>
            <dd className="font-medium text-gray-900">
              {log.user_name || log.user_email || 'System'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Action</dt>
            <dd className="font-medium text-gray-900">{log.action}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Entity</dt>
            <dd className="font-medium text-gray-900">{entityLabel}</dd>
          </div>
          {displayIp ? (
            <div>
              <dt className="text-gray-500">IP address</dt>
              <dd className="font-medium text-gray-900">
                {displayIp}
                {ipIsMapped ? (
                  <span className="block text-xs font-normal text-gray-500 mt-0.5">
                    Stored as {log.ip_address}
                  </span>
                ) : null}
              </dd>
            </div>
          ) : null}
        </dl>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {summary ? (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Summary</p>
              <p className="text-sm text-gray-900">{summary}</p>
            </div>
          ) : null}

          {changes.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Changes</p>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-600">
                    <tr>
                      <th className="px-3 py-2 font-medium">Setting</th>
                      <th className="px-3 py-2 font-medium">Before</th>
                      <th className="px-3 py-2 font-medium">After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {changes.map((row) => (
                      <tr key={`${row.key}-${row.label}`}>
                        <td className="px-3 py-2 text-gray-900 align-top">{row.label}</td>
                        <td className="px-3 py-2 text-gray-600 align-top whitespace-pre-wrap break-words">
                          {row.before ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-900 align-top whitespace-pre-wrap break-words">
                          {row.after ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">
              {changes.length > 0 || summary ? 'Technical details' : 'Details'}
            </p>
            <pre className="whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 font-mono">
              {rawDetails}
            </pre>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <Button type="button" variant="muted" size="sm" onClick={copyDetails}>
            Copy
          </Button>
          <Button type="button" variant="blue" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

function formatChangesText(changes) {
  return changes
    .map((c) => `${c.label}: ${c.before ?? '—'} → ${c.after ?? '—'}`)
    .join('\n')
}

function formatAppSource(source) {
  return HUB_APP_LABELS[source] || source || '—'
}

export default function HubAuditLog() {
  const { error: toastError } = useToast()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [appFilter, setAppFilter] = useState('')
  const [selectedLog, setSelectedLog] = useState(null)

  const closeDetail = useCallback(() => setSelectedLog(null), [])

  const logsTable = useClientDatatable(logs, {
    pageLength: 10,
    initialSort: { key: 'created_at', dir: 'desc' },
    getSortValue: (log, key) => {
      if (key === 'created_at') return parseDbDateTime(log.created_at)
      if (key === 'app_source') return formatAppSource(log.app_source)
      if (key === 'user') return log.user_name || log.user_email || 'System'
      if (key === 'entity') return formatAuditEntityLabel(log)
      if (key === 'details') {
        return formatAuditDetailsSummary(log) || formatAuditDetailsRaw(log)
      }
      return log[key]
    },
  })

  useEffect(() => {
    let cancelled = false

    async function loadAuditLogs() {
      setLoading(true)
      try {
        const params = { limit: 100 }
        if (appFilter) params.app_source = appFilter
        const result = await reportsAPI.audit(params)
        if (!cancelled) {
          setLogs(result.logs || result.audit_logs || [])
        }
      } catch (err) {
        if (!cancelled) {
          setLogs([])
          toastError(err.message || 'Failed to load audit logs')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadAuditLogs()
    return () => {
      cancelled = true
    }
  }, [appFilter])

  const appFilters = [
    { value: '', label: 'All apps' },
    { value: 'viva_hub', label: 'VIVA HUB' },
    { value: 'room_scheduler', label: 'Room Scheduler' },
    { value: 'crm', label: 'CRM' },
    { value: 'settings', label: 'Users' },
    { value: 'contact', label: 'Contact' },
  ]

  return (
    <HubLayout appName="Audit Log">
      <AuditLogDetailModal log={selectedLog} onClose={closeDetail} />

      <p className="text-gray-600 mb-4">
        System-wide activity from all VIVA HUB apps, including sign-in events.
      </p>

      <div className="mb-4 flex flex-wrap gap-2">
        {appFilters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setAppFilter(filter.value)}
            className={`px-3 py-1.5 rounded-md text-sm ${
              appFilter === filter.value
                ? 'bg-green-100 text-green-800'
                : 'bg-white border border-gray-200 text-gray-700'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-600">Loading audit logs...</p>}

      {!loading && (
        <DatatableCard footer={<DatatablePagination {...logsTable} itemLabel="entries" />}>
          <DatatableScroll>
          <table className={DATATABLE_TABLE_CLASS}>
            <thead className={DATATABLE_THEAD_CLASS}>
              <tr>
                <DatatableTh sortKey="created_at" sort={logsTable.sort} onSort={logsTable.toggleSort}>
                  When
                </DatatableTh>
                <DatatableTh sortKey="app_source" sort={logsTable.sort} onSort={logsTable.toggleSort}>
                  App
                </DatatableTh>
                <DatatableTh sortKey="user" sort={logsTable.sort} onSort={logsTable.toggleSort}>
                  User
                </DatatableTh>
                <DatatableTh sortKey="action" sort={logsTable.sort} onSort={logsTable.toggleSort}>
                  Action
                </DatatableTh>
                <DatatableTh sortKey="entity" sort={logsTable.sort} onSort={logsTable.toggleSort}>
                  Entity
                </DatatableTh>
                <DatatableTh sortKey="details" sort={logsTable.sort} onSort={logsTable.toggleSort}>
                  Details
                </DatatableTh>
              </tr>
            </thead>
            <tbody className={DATATABLE_TBODY_CLASS}>
              {logsTable.sortedItems.length === 0 ? (
                <DatatableEmpty colSpan={6} message="No audit logs found." />
              ) : (
                logsTable.rows.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/80">
                    <td className={DATATABLE_CELL_CLASS}>
                      {formatDbDateTime(log.created_at)}
                    </td>
                    <td className={DATATABLE_CELL_CLASS}>{formatAppSource(log.app_source)}</td>
                    <td className={DATATABLE_CELL_CLASS}>{log.user_name || log.user_email || 'System'}</td>
                    <td className={DATATABLE_CELL_CLASS}>{log.action}</td>
                    <td className={DATATABLE_CELL_CLASS}>
                      {formatAuditEntityLabel(log)}
                    </td>
                    <td className={`${DATATABLE_CELL_CLASS} max-w-xs`}>
                      <div className="flex items-start gap-2">
                        <span
                          className="min-w-0 flex-1 truncate text-gray-800"
                          title={formatAuditDetailsSummary(log) || formatAuditDetailsRaw(log)}
                        >
                          {formatAuditDetailsPreview(log)}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 text-sm font-medium text-green-700 hover:text-green-900 underline"
                          onClick={() => setSelectedLog(log)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </DatatableScroll>
        </DatatableCard>
      )}
    </HubLayout>
  )
}
